import { cn } from '@/lib/utils';

type ContributionMetricStripItem = {
  value: string | number;
  label: string;
  tone?: 'default' | 'emerald' | 'sky' | 'amber';
};

type ContributionMetricStripProps = {
  items: ContributionMetricStripItem[];
  className?: string;
};

const toneClass: Record<NonNullable<ContributionMetricStripItem['tone']>, string> = {
  default: 'text-foreground',
  emerald: 'text-success',
  sky: 'text-info',
  amber: 'text-brand',
};

export function ContributionMetricStrip({ items, className }: ContributionMetricStripProps) {
  return (
    <div
      className={cn(
        'relative max-w-2xl overflow-hidden rounded-[1.4rem] border border-border/40 bg-slate-900/[0.025] dark:bg-white/[0.025] px-4 py-3 backdrop-blur-xl',
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-primary/35 to-transparent" />
      <div className="grid gap-y-3 divide-y divide-border/40 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {items.map((item, index) => (
          <div key={`${item.label}-${index}`} className="px-1 py-2 sm:px-5 sm:py-1">
            <div className={cn('text-2xl font-semibold leading-none tracking-tight', toneClass[item.tone || 'default'])}>
              {item.value}
            </div>
            <div className="mt-2 text-[0.64rem] font-semibold uppercase tracking-[0.22em] text-muted-foreground/75">
              {item.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
type ContributionInfoStripItem = {
  eyebrow: string;
  text: string;
  tone?: 'default' | 'emerald' | 'sky' | 'amber';
};

type ContributionInfoStripProps = {
  items: ContributionInfoStripItem[];
  className?: string;
};

export function ContributionInfoStrip({ items, className }: ContributionInfoStripProps) {
  return (
    <div
      className={cn(
        'relative max-w-2xl overflow-hidden rounded-[1.4rem] border border-border/40 bg-slate-900/[0.025] dark:bg-white/[0.025] px-4 py-3 backdrop-blur-xl',
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="grid gap-y-3 divide-y divide-border/40 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {items.map((item, index) => (
          <div key={`${item.eyebrow}-${index}`} className="px-1 py-2 sm:px-4 sm:py-1">
            <div className={cn('text-[0.62rem] font-semibold uppercase tracking-[0.22em]', toneClass[item.tone || 'default'])}>
              {item.eyebrow}
            </div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground/78">
              {item.text}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

